import { FunctionComponent, memo, useState } from "react";
import { IProps } from "./remove-users-subscribed.types";
import Modal, { ModalContent, ModalFooter, ModalHeader, useModal } from "@edgetier/modal";
import toast from "react-hot-toast";
import { axios } from "utilities/axios";
import { Button } from "@edgetier/components";
import { faTrashAlt } from "@fortawesome/pro-solid-svg-icons";
import { getServerError } from "utilities/get-server-error";
import { IUser, Route } from "@edgetier/watchtower-types";
import { InfiniteData, useQueryClient } from "react-query";
import { IPaginated } from "@edgetier/types";
import { getTimeToRead } from "@edgetier/utilities";
import "./remove-users-subscribed.scss";

/**
 * Button component with modal, on click it will remove an user from tag alert.
 * @param props.userId
 * @param props.modelId
 */
const RemoveUsersSubscribed: FunctionComponent<IProps> = ({ userId, modelId }) => {
    const { closeModal, openModal, modalProps } = useModal();
    const [isLoading, setIsLoading] = useState(false);
    const queryClient = useQueryClient();

    /**
     * Make a DELETE request to API to unsubscribe an user
     */
    const unsubscribeUser = async () => {
        try {
            setIsLoading(true);
            await axios.delete(Route.UserUnsubscribe.replace(":userId", userId).replace(":modelId", modelId));

            // Remove user from cache
            queryClient.setQueriesData<InfiniteData<IPaginated<IUser>> | undefined>(
                [Route.ModelSubscriptions.replace(":modelId", modelId)],
                (users) => {
                    if (typeof users === "undefined") {
                        return undefined;
                    }
                    return {
                        ...users,
                        pages: users.pages.map((page) => {
                            return {
                                ...page,
                                items: page.items.filter((user) => user.userId !== userId),
                            };
                        }),
                    };
                }
            );
            const successMessage = "User successfully removed from tag alert";
            toast.success(successMessage, { duration: getTimeToRead(successMessage, 1000) });
            closeModal();
        } catch (serverError) {
            toast.error(getServerError(serverError));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div onClick={(event) => event.stopPropagation()} className="remove-users-subscribed">
            <Button styleName="negative" outline onClick={openModal}>
                Remove User
            </Button>
            <Modal {...modalProps}>
                <ModalHeader>
                    <h2>Remove User</h2>
                </ModalHeader>
                <ModalContent>
                    <p>Are you sure you want to remove this user from the tag alert?</p>
                </ModalContent>
                <ModalFooter>
                    <Button icon={faTrashAlt} isLoading={isLoading} onClick={unsubscribeUser} styleName="negative">
                        Yes, Remove User
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default memo(RemoveUsersSubscribed);
